import { t } from "@hakili/i18n";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { Button, type ButtonVariant } from "./Button.js";
import { Chip } from "./Chip.js";
import { Field } from "./Field.js";
import { Modal } from "./Modal.js";
import { classes } from "./classes.js";

/**
 * `P1-UIK-025`, round 203: **the marked-stub convention, once, for every app.**
 *
 * The founder's frontend-first turn says every screen the product will have is
 * present and reachable, and a function not yet built renders as a VISIBLE,
 * clearly marked, inert affordance bound to its real screen location , never
 * hidden, never omitted , so real functionality drops in behind it without
 * moving the furniture.
 *
 * ## The convention is the MOCKUPS' OWN, and that is why it is not invented
 *
 * `DemandeFonctionnaliteModal` appears in all four recorded mockups ,
 * `hakili-client.jsx`, `hakili-bank.jsx`, `hakili-dealer.jsx`,
 * `hakili-console.jsx` , wired to each NavRail through `onDemande`, asking the
 * same three questions. This file is that component, made shared rather than
 * copied four times, and `AVenir` is the affordance that opens it.
 *
 * **So a stub is not a placeholder: it is an instrument.** The founder walks
 * every app daily; each thing he reaches for and cannot get writes down what he
 * was trying to do, what was missing, and what it would have let him do. A
 * greyed-out button records nothing.
 *
 * ## Why a CONTEXT and not a prop
 *
 * *"every unbuilt action across every app looks and behaves the same"* is the
 * order's own clause, and a prop threaded through six apps is six chances to
 * thread it differently. The provider holds one modal at an app's root; every
 * `AVenir` beneath it reaches the same one and cannot reach another.
 *
 * **It refuses rather than degrades when the provider is absent.**
 * {@link useDemandeFonctionnalite} throws, because an `AVenir` that silently
 * did nothing on click would be exactly the dead affordance this convention
 * exists to abolish , and it would look identical to a working one.
 *
 * ## `contexte` is the address, and it is written the same way everywhere
 *
 * `"<app>:<screen>:<action>"`. It travels with the request so a demand can be
 * read back to a screen position rather than to a memory of one.
 *
 * **Zero business logic**, per 4.6.2: this file knows what an unbuilt action
 * looks like. It does not know what a dossier is.
 */

/** The address shape every stub declares: `"<app>:<screen>:<action>"`. */
export const CONTEXTE_MOTIF = /^[a-z-]+:[a-z-]+:[a-z-]+$/u;

export interface DemandeFonctionnalite {
  /** What the founder was trying to do, in his words. */
  readonly faire: string;
  /** What was missing. */
  readonly manque: string;
  /** What it would let him do. */
  readonly permettrait: string;
  /** Where he was standing: `"<app>:<screen>:<action>"`. */
  readonly contexte: string;
}

interface DemandeContexte {
  readonly ouvrir: (contexte: string) => void;
}

const Contexte = createContext<DemandeContexte | null>(null);

/**
 * The hook an `AVenir` uses, and a loud refusal when no provider stands above
 * it , see this file's note on why silence would be the worse failure.
 */
export function useDemandeFonctionnalite(): DemandeContexte {
  const value = useContext(Contexte);
  if (value === null) {
    throw new Error(
      "AVenir needs a DemandeFonctionnaliteProvider above it: a stub with nowhere to send a demand is a dead affordance.",
    );
  }
  return value;
}

export interface DemandeFonctionnaliteProviderProps {
  readonly children: ReactNode;
  /**
   * Where a demand goes. Round 203 hands it to the console's `fonctionnalites`
   * screen through the app; until that screen exists an app may keep them in
   * memory, which is still a record of the walk.
   */
  readonly envoyer?: (demande: DemandeFonctionnalite) => void | Promise<void>;
}

export function DemandeFonctionnaliteProvider({
  children,
  envoyer,
}: DemandeFonctionnaliteProviderProps): React.JSX.Element {
  const [contexte, setContexte] = useState<string | null>(null);
  const [faire, setFaire] = useState("");
  const [manque, setManque] = useState("");
  const [permettrait, setPermettrait] = useState("");
  const [envoye, setEnvoye] = useState(false);
  /**
   * `OD-18`, `round F5`: **a refusal is shown, and « Demande transmise » is
   * said only after the door said so.**
   *
   * Until this round `envoyer` was fire-and-forget , `void envoyer?.(...)`
   * followed by `setEnvoye(true)` , which was harmless while every app kept
   * demands in memory and became the round's own lie the moment one of them
   * called an api: a demand the door turned away would still have read
   * « Demande transmise ». So the promise is awaited and a rejection stays on
   * the modal, with what the caller wrote still in the boxes.
   */
  const [refus, setRefus] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  /**
   * The guard against a second press, in a REF and not in the state beside it.
   *
   * `enCours` is what the button reads; a second click landing in the same tick
   * would still see `false` there and send the demand twice. The ref is written
   * synchronously, so it is the one that can refuse.
   */
  const envoiEnCours = useRef(false);

  const ouvrir = useCallback((cible: string) => {
    setContexte(cible);
    setFaire("");
    setManque("");
    setPermettrait("");
    setEnvoye(false);
    setRefus(null);
    setEnCours(false);
    envoiEnCours.current = false;
  }, []);

  const fermer = useCallback(() => {
    setContexte(null);
  }, []);

  const value = useMemo<DemandeContexte>(() => ({ ouvrir }), [ouvrir]);

  const soumettre = useCallback(async (): Promise<void> => {
    if (contexte === null || envoiEnCours.current) return;
    envoiEnCours.current = true;
    setRefus(null);
    setEnCours(true);
    try {
      await envoyer?.({ faire, manque, permettrait, contexte });
      setEnvoye(true);
    } catch (failure) {
      // The api's OWN words when it gave any , a message written here would be
      // this package guessing what a door refused for.
      const dit = failure instanceof Error ? failure.message.trim() : "";
      setRefus(dit === "" ? t("ui.demande.echec") : dit);
    } finally {
      envoiEnCours.current = false;
      setEnCours(false);
    }
  }, [contexte, envoyer, faire, manque, permettrait]);

  return (
    <Contexte.Provider value={value}>
      {children}
      <Modal
        open={contexte !== null}
        title={envoye ? t("ui.demande.transmise") : t("ui.demande.titre")}
        closeLabel={t("ui.demande.fermer")}
        onClose={fermer}
        actions={
          envoye ? (
            <Button onClick={fermer}>{t("ui.demande.fermer")}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={fermer}>
                {t("ui.demande.annuler")}
              </Button>
              <Button
                onClick={() => {
                  void soumettre();
                }}
              >
                {enCours ? t("ui.demande.envoiEnCours") : t("ui.demande.envoyer")}
              </Button>
            </>
          )
        }
      >
        {envoye ? (
          <p>{t("ui.demande.merci")}</p>
        ) : (
          <div className="flex flex-col gap-4">
            {/* The screen position travels with the demand, so a later reader
                lands where the founder was standing rather than guessing. */}
            <p className="font-mono text-xs text-gray-400">{contexte ?? ""}</p>
            {refus === null ? null : (
              <p role="alert" className="text-sm text-rose-600" data-testid="demande-refus">
                {refus}
              </p>
            )}
            <Field label={t("ui.demande.faire")} htmlFor="demande-faire">
              <textarea
                id="demande-faire"
                rows={2}
                value={faire}
                onChange={(event) => {
                  setFaire(event.target.value);
                }}
                className="w-full rounded-lg border border-gray-200 p-2 text-sm"
              />
            </Field>
            <Field label={t("ui.demande.manque")} htmlFor="demande-manque">
              <textarea
                id="demande-manque"
                rows={2}
                value={manque}
                onChange={(event) => {
                  setManque(event.target.value);
                }}
                className="w-full rounded-lg border border-gray-200 p-2 text-sm"
              />
            </Field>
            <Field label={t("ui.demande.permettrait")} htmlFor="demande-permettrait">
              <textarea
                id="demande-permettrait"
                rows={2}
                value={permettrait}
                onChange={(event) => {
                  setPermettrait(event.target.value);
                }}
                className="w-full rounded-lg border border-gray-200 p-2 text-sm"
              />
            </Field>
          </div>
        )}
      </Modal>
    </Contexte.Provider>
  );
}

export interface AVenirProps {
  /** The label the REAL control will carry. It does not change when the function lands. */
  readonly children: ReactNode;
  /** `"<app>:<screen>:<action>"`, the address this affordance answers to. */
  readonly contexte: string;
  readonly variant?: ButtonVariant;
  readonly className?: string;
}

/**
 * An action that is not built yet, standing in its real place and saying so.
 *
 * It is a live button , not `disabled` , because a disabled control tells the
 * walker nothing and collects nothing. The chip is what marks it, and the chip
 * is the only thing that disappears when the real handler arrives.
 */
export function AVenir({
  children,
  contexte,
  variant = "secondary",
  className,
}: AVenirProps): React.JSX.Element {
  const { ouvrir } = useDemandeFonctionnalite();
  return (
    // **`data-contexte` is on the DOM and not only in the closure**, and round
    // 203's own spec is what bought it: the first cut passed the address to
    // `ouvrir` alone, so nothing could see which stub was which without
    // clicking it. An address only a click can reveal cannot be audited, cannot
    // be swept for duplicates, and cannot be read off a screenshot of the
    // founder's walk.
    <span data-contexte={contexte} className="inline-flex">
      <Button
        variant={variant}
        className={classes("inline-flex items-center gap-2", className)}
        onClick={() => {
          ouvrir(contexte);
        }}
      >
        {children}
        <Chip tone="attention">{t("ui.stub.marque")}</Chip>
      </Button>
    </span>
  );
}

export interface EcranAVenirProps {
  /** The screen's own title, so the furniture is already in its final place. */
  readonly titre: ReactNode;
  /** One sentence on what this screen will do, from its mockup. */
  readonly resume: ReactNode;
  /** `"<app>:<screen>:<action>"` for the screen's primary action. */
  readonly contexte: string;
  /** The actions the mockup draws here, each an `AVenir` unless built. */
  readonly children?: ReactNode;
}

/**
 * A whole screen that is present, reachable and honest about being unbuilt.
 *
 * The title and the layout are the FINAL ones: when the screen is built, this
 * wrapper is replaced and nothing around it moves, which is the order's
 * *"real functionality drops in behind it without moving the furniture"*.
 */
export function EcranAVenir({ titre, resume, contexte, children }: EcranAVenirProps): React.JSX.Element {
  return (
    <section className="flex flex-col gap-4" data-testid="ecran-a-venir" data-contexte={contexte}>
      <header className="flex items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold text-gray-900">{titre}</h1>
        <Chip tone="attention">{t("ui.stub.marque")}</Chip>
      </header>
      <p className="text-sm text-gray-500">{resume}</p>
      {children ?? <AVenir contexte={contexte}>{t("ui.demande.ouvrir")}</AVenir>}
    </section>
  );
}
